import React, { useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { labels } from '../data/content';

const ROUND_BUTTON =
  'absolute flex h-11 w-11 items-center justify-center rounded-full text-stone-200 transition-colors hover:bg-white/10 hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white';

function Chevron({ direction }) {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d={direction === 'left' ? 'm15 18-6-6 6-6' : 'm9 18 6-6-6-6'} />
    </svg>
  );
}

/* 전체 화면 이미지 보기. 바깥 클릭·Esc로 닫고, 여러 장이면 방향키로 넘긴다. */
export default function Lightbox({ title, images, index, onIndexChange, onClose }) {
  const { t } = useLanguage();
  const closeRef = useRef(null);
  const count = images.length;
  const image = images[index];

  // 열릴 때 포커스를 옮기고 배경 스크롤을 막았다가, 닫히면 원래대로 돌린다
  useEffect(() => {
    const previous = document.activeElement;
    const { overflow } = document.body.style;
    document.body.style.overflow = 'hidden';
    if (closeRef.current) closeRef.current.focus();
    return () => {
      document.body.style.overflow = overflow;
      if (previous && previous.focus) previous.focus();
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (count > 1 && e.key === 'ArrowRight') onIndexChange((index + 1) % count);
      if (count > 1 && e.key === 'ArrowLeft') onIndexChange((index - 1 + count) % count);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [index, count, onClose, onIndexChange]);

  const step = (delta) => (e) => {
    e.stopPropagation();
    onIndexChange((index + delta + count) % count);
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex cursor-zoom-out items-center justify-center bg-stone-950/85 p-4 backdrop-blur-sm md:p-12"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={image.alt}
    >
      <figure className="flex w-full max-w-5xl flex-col items-center">
        <img src={image.src} alt={image.alt} className="max-h-[80vh] w-auto max-w-full rounded-sm shadow-2xl" />
        <figcaption className="mt-3 text-center text-sm tabular-nums text-stone-300">
          {title || image.alt}
          {count > 1 && ` · ${index + 1} / ${count}`}
        </figcaption>
      </figure>

      {count > 1 && (
        <>
          <button type="button" onClick={step(-1)} aria-label={t(labels.prev)} className={`${ROUND_BUTTON} left-2 top-1/2 -translate-y-1/2 md:left-4`}>
            <Chevron direction="left" />
          </button>
          <button type="button" onClick={step(1)} aria-label={t(labels.next)} className={`${ROUND_BUTTON} right-2 top-1/2 -translate-y-1/2 md:right-4`}>
            <Chevron direction="right" />
          </button>
        </>
      )}

      <button ref={closeRef} type="button" onClick={onClose} aria-label={t(labels.close)} className={`${ROUND_BUTTON} right-3 top-3 text-3xl leading-none`}>
        ×
      </button>
    </div>
  );
}
